import React, { useState, useEffect, useRef } from 'react';
import './CoachMessaging.css';

interface Message {
  id: string;
  sender: string;
  text: string;
  timestamp: Date;
  chatId: string;
}

interface Client {
  relationship_id: number;
  client_id: string;
  full_name: string;
  email: string;
  assigned_at: string;
  relationship_notes?: string;
  online?: boolean;
  unread?: number;
  lastMessage?: string;
}

interface CoachMessagingProps {
  clients: Client[];
  coachId: string;
  coachName: string;
}

interface ClientUnreadCount {
  client_id: string;
  client_name: string;
  unread_count: number;
  last_message_time: string;
  last_message_preview: string;
}

const CoachMessaging: React.FC<CoachMessagingProps> = ({ clients, coachId, coachName }) => {
  const [activeChat, setActiveChat] = useState<string>('');
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [clientUnreadCounts, setClientUnreadCounts] = useState<ClientUnreadCount[]>([]);
  const [totalUnread, setTotalUnread] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Enhanced messaging state
  const [messageError, setMessageError] = useState<string>('');
  const [sendingStatus, setSendingStatus] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle');
  const [networkStatus, setNetworkStatus] = useState<'online' | 'offline'>('online');
  const [failedMessages, setFailedMessages] = useState<Set<string>>(new Set());

  // Message validation constants
  const MAX_MESSAGE_LENGTH = 1000;
  const WARNING_THRESHOLD = 900;

  const API_BASE = `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001'}/api`;

  // Helper function for secure requests
  const makeSecureRequest = async (endpoint: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('auth_token');
    return fetch(`${process.env.REACT_APP_API_BASE_URL}${endpoint}`, {
      ...options,
      credentials: 'include',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });
  };

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    const timeouts = [
      setTimeout(() => scrollToBottom(), 50),
      setTimeout(() => scrollToBottom(), 200),
      setTimeout(() => scrollToBottom(), 500)
    ];

    return () => {
      timeouts.forEach(timeout => clearTimeout(timeout));
    };
  }, [messages]);

  // Additional effect to scroll when active chat changes
  useEffect(() => {
    if (activeChat && messages.length > 0) {
      setTimeout(() => scrollToBottom(), 300);
    }
  }, [activeChat]);

  // Load messages when active chat changes
  useEffect(() => {
    if (activeChat) {
      loadMessages(activeChat);
    }
  }, [activeChat]);

  // Start polling for notifications on component mount
  useEffect(() => {
    loadUnreadCounts();

    pollIntervalRef.current = setInterval(() => {
      loadUnreadCounts();
    }, 5 * 60 * 1000); // 5 minutes

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  // Update total unread when client counts change
  useEffect(() => {
    const total = clientUnreadCounts.reduce((sum, client) => sum + client.unread_count, 0);
    setTotalUnread(total);
  }, [clientUnreadCounts]);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      const container = messagesEndRef.current.closest('.messages-container') as HTMLElement;

      if (container) {
        container.scrollTop = container.scrollHeight;

        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({
            behavior: 'auto',
            block: 'end',
            inline: 'nearest'
          });
        }, 10);

        setTimeout(() => {
          container.scrollTop = container.scrollHeight;
        }, 100);
      }
    }
  };

  const loadMessages = async (clientId: string) => {
    try {
      setLoading(true);
      const response = await makeSecureRequest(`/api/messages/history?other_user_id=${clientId}`);

      if (response.ok) {
        const data = await response.json();
        const sortedMessages = (data.messages || []).sort((a: Message, b: Message) => {
          const timestampA = new Date(a.timestamp).getTime();
          const timestampB = new Date(b.timestamp).getTime();
          return timestampA - timestampB;
        });
        setMessages(sortedMessages);

        await markMessagesAsRead(clientId);
        loadUnreadCounts();
      } else {
        setMessages([]);
      }
    } catch (error) {
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const markMessagesAsRead = async (clientId: string) => {
    try {
      await makeSecureRequest('/api/messages/mark-as-read', {
        method: 'POST',
        body: JSON.stringify({
          sender_id: clientId
        })
      });

      setTimeout(async () => {
        const response = await makeSecureRequest('/api/messages/has-unread');

        if (response.ok) {
          const data = await response.json();
          if (!data.has_unread) {
            window.dispatchEvent(new CustomEvent('clearMessageNotifications'));
          }
        }
      }, 100);

    } catch (error) {
      console.error('Failed to mark messages as read:', error);
    }
  };

  const loadUnreadCounts = async () => {
    try {
      const response = await makeSecureRequest('/api/messages/client-unread-counts');

      if (response.ok) {
        const data = await response.json();
        setClientUnreadCounts(data.client_unread_counts || []);
      }
    } catch (error) {
      console.error('Failed to load unread counts:', error);
    }
  };

  const getClientUnreadCount = (clientId: string): number => {
    const clientData = clientUnreadCounts.find(c => c.client_id === clientId);
    return clientData?.unread_count || 0;
  };

  const validateMessage = (text: string): string | null => {
    if (!text.trim()) return 'Message cannot be empty';
    if (text.length > MAX_MESSAGE_LENGTH) return `Message too long (${text.length}/${MAX_MESSAGE_LENGTH} characters)`;
    return null;
  };

  const handleMessageChange = (text: string) => {
    setMessage(text);
    setMessageError('');

    const error = validateMessage(text);
    if (error) {
      setMessageError(error);
    }
  };

  const retryFailedMessage = async (messageId: string, messageText: string) => {
    setFailedMessages(prev => {
      const newSet = new Set(prev);
      newSet.delete(messageId);
      return newSet;
    });

    await sendMessageInternal(messageText, messageId);
  };

  const sendMessageInternal = async (messageText: string, messageId?: string) => {
    const finalMessageId = messageId || `temp_${Date.now()}`;

    try {
      setSendingStatus('sending');
      setNetworkStatus('online');

      const response = await makeSecureRequest('/api/messages/send', {
        method: 'POST',
        body: JSON.stringify({
          receiver_id: activeChat,
          content: messageText
        })
      });

      if (response.ok) {
        setSendingStatus('sent');
        setTimeout(() => setSendingStatus('idle'), 2000);
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      setSendingStatus('failed');
      setNetworkStatus('offline');
      setFailedMessages(prev => new Set(prev).add(finalMessageId));
      setMessageError('Failed to send message. Check your connection.');

      setMessages(prev => prev.filter(msg => msg.id !== finalMessageId));
    }
  };

  const sendMessage = async () => {
    if (!message.trim() || !activeChat) return;

    const error = validateMessage(message);
    if (error) {
      setMessageError(error);
      return;
    }

    const messageText = message.trim();
    const tempMessageId = `temp_${Date.now()}`;
    setMessage('');
    setMessageError('');

    const tempMessage: Message = {
      id: tempMessageId,
      sender: coachName,
      text: messageText,
      timestamp: new Date(),
      chatId: activeChat
    };
    setMessages(prev => [...prev, tempMessage]);

    await sendMessageInternal(messageText, tempMessageId);
  };

  const formatTime = (date: Date | string) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (!dateObj || isNaN(dateObj.getTime())) {
      return 'Invalid time';
    }
    return dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatMessageDate = (date: Date | string) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (!dateObj || isNaN(dateObj.getTime())) {
      return 'Invalid date';
    }

    const today = new Date();
    const messageDate = new Date(dateObj);

    if (messageDate.toDateString() === today.toDateString()) {
      return 'Today';
    }

    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    if (messageDate.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    }

    return messageDate.toLocaleDateString([], {
      month: 'short',
      day: 'numeric',
      year: messageDate.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
    });
  };

  const activeClient = clients.find(client => client.client_id === activeChat);
  const chatMessages = messages
    .filter(msg => msg.chatId === activeChat)
    .sort((a, b) => {
      const timestampA = new Date(a.timestamp).getTime();
      const timestampB = new Date(b.timestamp).getTime();
      return timestampA - timestampB;
    });

  return (
    <div className="coach-messaging">
      <div className="messaging-layout">
        {/* Client List Sidebar */}
        <div className="client-sidebar">
          <div className="client-sidebar-header">
            <h4>Your Clients</h4>
            {totalUnread > 0 && (
              <div className="total-unread-badge" title={`${totalUnread} unread messages`}>
                {totalUnread}
              </div>
            )}
          </div>
          <div className="client-list">
            {clients.length === 0 ? (
              <p className="no-clients">No clients assigned yet.</p>
            ) : (
              clients.map(client => {
                const unreadCount = getClientUnreadCount(client.client_id);
                const hasUnread = unreadCount > 0;

                return (
                  <div
                    key={client.client_id}
                    onClick={() => setActiveChat(client.client_id)}
                    className={`client-item ${activeChat === client.client_id ? 'active' : ''} ${hasUnread ? 'has-unread' : ''}`}
                  >
                    <div className="client-avatar">
                      {client.full_name.charAt(0).toUpperCase()}
                      {hasUnread && <div className="avatar-notification-dot"></div>}
                    </div>
                    <div className="client-details">
                      <div className="client-name">{client.full_name}</div>
                      <div className="client-last-message">
                        {clientUnreadCounts.find(c => c.client_id === client.client_id)?.last_message_preview ||
                         client.lastMessage ||
                         'Start a conversation...'}
                      </div>
                    </div>
                    {hasUnread && (
                      <div className="unread-badge" title={`${unreadCount} unread messages`}>
                        {unreadCount}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Chat Area */}
        <div className="chat-area">
          {activeChat ? (
            <>
              {/* Chat Header */}
              <div className="chat-header">
                <div className="client-avatar">
                  {activeClient?.full_name.charAt(0).toUpperCase()}
                </div>
                <div className="client-info">
                  <h4>{activeClient?.full_name}</h4>
                  <p>{activeClient?.email}</p>
                </div>
              </div>

              {/* Messages */}
              <div className="messages-container">
                {loading ? (
                  <div className="loading-messages">Loading messages...</div>
                ) : chatMessages.length === 0 ? (
                  <div className="no-messages">
                    <p>No messages yet</p>
                    <p>Start the conversation with {activeClient?.full_name}!</p>
                  </div>
                ) : (
                  chatMessages.map(msg => (
                    <div
                      key={msg.id}
                      className={`message-bubble ${msg.sender === coachName ? 'own-message' : 'other-message'}`}
                    >
                      <div className="message-content">
                        <p>{msg.text}</p>
                        <div className="message-metadata">
                          <span className="message-date">{formatMessageDate(msg.timestamp)}</span>
                          <span className="message-time">{formatTime(msg.timestamp)}</span>
                        </div>
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Message Input */}
              <div className="message-input-area">
                {messageError && (
                  <div className="message-error">
                    ⚠️ {messageError}
                  </div>
                )}

                {networkStatus === 'offline' && (
                  <div className="network-status offline">
                    🔴 Offline - Messages will be sent when connection is restored
                  </div>
                )}

                <div className="input-container">
                  <input
                    type="text"
                    placeholder={`Message ${activeClient?.full_name}...`}
                    value={message}
                    onChange={(e) => handleMessageChange(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                    className={`message-input ${messageError ? 'error' : ''} ${message.length > WARNING_THRESHOLD ? 'warning' : ''}`}
                    disabled={loading || sendingStatus === 'sending'}
                    maxLength={MAX_MESSAGE_LENGTH}
                  />

                  <div className={`character-count ${message.length > WARNING_THRESHOLD ? 'warning' : ''} ${message.length >= MAX_MESSAGE_LENGTH ? 'error' : ''}`}>
                    {message.length}/{MAX_MESSAGE_LENGTH}
                  </div>

                  <button
                    onClick={sendMessage}
                    disabled={!message.trim() || loading || sendingStatus === 'sending' || !!messageError}
                    className={`send-button ${sendingStatus}`}
                  >
                    {sendingStatus === 'sending' ? '⏳' : sendingStatus === 'sent' ? '✅' : sendingStatus === 'failed' ? '❌' : 'Send'}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="no-chat-selected">
              <div className="placeholder-content">
                <h3>💬 Coach Messaging</h3>
                <p>Select a client to start messaging</p>
                {clients.length === 0 && (
                  <p className="helper-text">
                    Assign clients in the Clients tab to start messaging them.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CoachMessaging;
